import React from 'react'
import { userQuery } from '@/api/users'
import { createFileRoute } from '@tanstack/react-router'
import { tagsQuery, foodRestrictionsQuery, ingredientsQuery } from '@/api'
import {
  energyDistributionQuery,
  userMealPlanQuery,
  userNutritionalNeedsQuery,
} from '@/api/mealPlan'
import { UserProfilePanel } from '@/features/UserProfilePanel/UserProfilePanel'
import { UserNutritionalNeeds } from '@/api/types'
import WeekMealPlan from '@/features/meal-recommendations/MealPlan/WeekMealPlan'
import UserMealPlan from '@/features/meal-recommendations/MealPlan/CreateMealPlan'
import { formatISO } from 'date-fns'

export type UserSearchParams = {
  mealPlanDate?: string
}

export function formatDate(date: Date): string {
  return formatISO(date, { representation: 'date' })
}

export const Route = createFileRoute('/users/$dbcontext/$id')({
  validateSearch: ({ mealPlanDate }): UserSearchParams => {
    return { mealPlanDate: mealPlanDate as string }
  },
  loaderDeps: ({ search }) => ({
    ...search,
    mealPlanDate: search.mealPlanDate || formatDate(new Date()),
  }),
  loader: async ({ context: { queryClient }, params, deps }) => {
    const [userProfile, tags, allergens, ingredients, energyDistribution, weekPlan] =
      await Promise.all([
        queryClient.ensureQueryData(userQuery(params.dbcontext, params.id)),
        queryClient.ensureQueryData(tagsQuery),
        queryClient.ensureQueryData(foodRestrictionsQuery),
        queryClient.ensureQueryData(ingredientsQuery()),
        queryClient.ensureQueryData(energyDistributionQuery),
        queryClient.ensureQueryData(
          userMealPlanQuery(params.dbcontext, params.id, deps.mealPlanDate),
        ),
      ])
    let nutritionalNeeds: UserNutritionalNeeds
    try {
      nutritionalNeeds = await queryClient.ensureQueryData(
        userNutritionalNeedsQuery(params.dbcontext, params.id),
      )
    } catch (error) {
      nutritionalNeeds = {
        mealsKcalNeed: {},
        nutrientNeeds: {
          KCAL: 0,
          CARB: 0,
          PROTEIN: 0,
          FAT: 0,
          CHOLESTEROL: 0,
          FIBER: 0,
          SALT: 0,
          SUGAR: 0,
          IRON: 0,
        },
      }
    }

    return {
      userProfile,
      nutritionalNeeds,
      tags,
      allergens,
      ingredients,
      energyDistribution,
      weekPlan,
      mealPlanDate: deps.mealPlanDate,
    }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const {
    userProfile,
    nutritionalNeeds,
    energyDistribution,
    tags,
    allergens,
    weekPlan,
    mealPlanDate,
  } = Route.useLoaderData()

  return (
    <div>
      <UserProfilePanel
        isReadonly={true}
        userProfile={userProfile}
        nutritionalNeeds={nutritionalNeeds}
        energyDistribution={energyDistribution}
        tags={tags}
        allergens={allergens}
      />
      <UserMealPlan
        userProfile={userProfile}
        householdMembers={userProfile.household.users || []}
        userMealPlan={weekPlan}
        planDate={mealPlanDate}
        userNutritionalNeeds={nutritionalNeeds}
        allowSimulate={false}
      />
    </div>
  )
}
